/**
 * Module dependencies.
 */

import { Box, color, units } from '@untile/react-components';
import { FormData, createUser } from 'src/api/user/create-user';
import { NextPage } from 'next/types';
import { RouterLink } from 'src/components/core';
import { authRoutes } from 'src/core/routes';
import { getNetworkErrorTranslatedKey } from 'src/core/utils/errors';
import { theme } from 'styled-tools';
import { useAuth } from 'src/context/auth/context';
import { useCallback } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useMutation } from 'react-query';
import { useNewsletter } from 'src/api/form/use-newsletter';
import { useRouter } from 'next/router';
import { useSnackbar } from 'src/context/snackbar/context';
import BaseAuthPage from 'src/components/pages/auth';
import SignUpForm from 'src/components/sign-up/sign-up-form';
import Trans from 'next-translate/Trans';
import omit from 'lodash/omit';
import qs from 'qs';
import styled from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * `StyledRouterLink` styled component.
 */

const StyledRouterLink = styled(RouterLink)`
  color: ${color('black')};
  text-decoration: underline;
  transition: opacity ${theme('animations.defaultTransition')};

  &:focus,
  &:hover {
    opacity: 0.8;
  }
`;

/**
 * `useSignUp` hook.
 */

function useSignUp() {
  const { onClearToken, onUpdateToken } = useAuth();
  const { showMessage } = useSnackbar();
  const { translate } = useTranslate();
  const router = useRouter();
  const { isLoading, mutateAsync: mutateAuthUser } = useMutation(createUser);

  const handleOnError = useCallback(
    ({ status }) => {
      const errorCode = status === 409 ? 'unauthorized' : 'default';
      const translatedKey = getNetworkErrorTranslatedKey('signUp', errorCode);

      showMessage(translate(`errors:${translatedKey}`), {
        appearance: 'error'
      });

      if (status === 409) {
        onClearToken();
        router.push(authRoutes.signIn);
      }
    },
    [onClearToken, router, showMessage, translate]
  );

  const { executeRecaptcha } = useGoogleReCaptcha();
  const { isSubmitting: isSubscribingNewsletter, onSubscribeNewsletter } =
    useNewsletter({ hideMessages: true });

  const handleSubscribeNewsletter = useCallback(
    async email => {
      const recaptchaToken = await executeRecaptcha('signupNewsletter').then(
        token => token
      );

      const formData = { email, recaptchaToken };

      onSubscribeNewsletter(formData);
    },
    [executeRecaptcha, onSubscribeNewsletter]
  );

  const handleCreateUser = useCallback(
    (formData: FormData) => {
      const subcribeToNewsletter = formData.info.subcribeToNewsletter;
      const normalizedFormData = omit(formData, [
        'info.subscribeToNewsletter'
      ]) as FormData;

      return mutateAuthUser(normalizedFormData, {
        onError: handleOnError,
        onSuccess: ({ token }) => {
          onUpdateToken(token);
          router.push({
            pathname: authRoutes.checkEmail,
            query: qs.stringify({ email: formData.email })
          });

          if (subcribeToNewsletter) {
            handleSubscribeNewsletter(formData.email);
          }
        }
      });
    },
    [
      handleOnError,
      handleSubscribeNewsletter,
      mutateAuthUser,
      onUpdateToken,
      router
    ]
  );

  return {
    isSubmitting: isLoading || isSubscribingNewsletter,
    onCreateUser: handleCreateUser
  };
}

/**
 * `SignUp` page.
 */

const SignUp: NextPage = () => {
  const { translate } = useTranslate();
  const { isSubmitting, onCreateUser } = useSignUp();

  return (
    <BaseAuthPage
      description={translate('sign-up:description')}
      title={translate('sign-up:title')}
    >
      <Box marginBottom={units(5)}>
        <SignUpForm
          isSubmitting={isSubmitting}
          onSubmit={onCreateUser}
        />
      </Box>

      <Trans
        components={[
          <StyledRouterLink
            href={authRoutes.signIn}
            key={'sign-up-link'}
          >
            {translate('common:actions.signIn')}
          </StyledRouterLink>
        ]}
        i18nKey={'sign-up:signIn'}
      />
    </BaseAuthPage>
  );
};

/**
 * Export `SignUp` page.
 */

export default SignUp;
